"use client";
import { useEffect, useState } from "react";
import InfiniteScroll from "@/components/InfiniteScroll";
import EventList from "@/components/views/events/EventList";
import { EventLoading } from "@/components/views/events/EventsSection";
import { DEFAULT_DATA_LIMIT } from "@/@core/constants/pagination";
import apiFetch from "@/lib/fetch";
import { useEventContext } from "@/context/EventContext";
import NotFound from "../../list/NotFound";
import { useSearchContext } from "@/lib/hooks/searchContext/searchStore";
import HeaderDataCount from "@/components/views/common/HeaderDataCount";
import { useHeader } from "@/hooks/headerContext";
import { useTranslation } from "@/hooks/translationContext";

interface Props extends SearchProps {
  data: {
    response: [];
    summary: any;
  };
}

export default function EventClientSection(props: Props) {
  const { t } = useTranslation();
  const { eventFilters, clearFilters } = useEventContext();

  const [data, setData] = useState<any>(props.data?.response);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const { clearFilters: clearSearchFilters } = useHeader()

  const fetchEvents = async (page: number, newData: boolean) => {
    let url = `event?index=${page}`;

    if (eventFilters.service) url += `&eventService=${eventFilters.service}`;
    if (eventFilters.date) url += `&date=${eventFilters.date}`;
    if (props.service) url += `&service=${props.service}`;
    if (props.district) url += `&district=${props.district}`;
    if (props.venue) url += `&venue=${props.venue}`;

    const events = await apiFetch({
      route: url,
    });

    const response = newData ? data.concat(events.response) : events.response;

    setData(response);
    setIsLoading(false);
    if (events?.response?.length < DEFAULT_DATA_LIMIT) setHasMore(false);
  };

  const next = (page: number) => {
    if (isLoading) return;

    setIsLoading(true);
    fetchEvents(page, true);
  };

  useEffect(() => {
    setData(props.data?.response);

    if (props.data?.response?.length < DEFAULT_DATA_LIMIT) setHasMore(false);
    else setHasMore(true);
  }, [props.data]);

  useEffect(() => {
    fetchEvents(0, false);
  }, [eventFilters]);

  if (!props.data?.response || !props.data?.response?.length)
    return (
      <div className="py-4">
        <HeaderDataCount count={props.data.summary} />
        <NotFound
          title={t("event-not-found-title")}
          searchText={t("event-search-placeholder")}
          showAllText={t("event-list-all-button")}
          clearFilter={() => clearSearchFilters()}
        />
      </div>
    );

  return (
    <>
      <InfiniteScroll
        isLoading={isLoading}
        skeletonElementId="event-card-skeleton"
        hasMore={hasMore}
        loading={<EventLoading />}
        next={next}
        dataLength={data?.length || 0}
      >
        <div className="grid justify-items-center grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-y-5 gap-x-4 py-4">
          <EventList titleHeadingLevel="h2" events={data} />
        </div>
      </InfiniteScroll>
    </>
  );
}
